// Bossa nova lofi (docs/AUDIO_DESIGN.md §12): a nylon guitar playing the João Gilberto batida (thumb on 1 and 3,
// syncopated finger chords), an upright bass on the root and fifth with its pickups, cross-stick clave, shaker and a
// soft surdo kick, a flute or a second nylon guitar on the melody. Straight 16ths, laid back under the city.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote, playedChords } from '../harmony.js';
import { createNylon } from './bossa-guitar.js';
import { createUpright, createFlute, createBossaKit } from './bossa-band.js';

const mod12 = n => ((n % 12) + 12) % 12;
const rnd = (r, a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
const E_IX = { chill: 0, balanced: 1, upbeat: 2 };

/* ---------------- guitar voicings: 4 notes, the thumb's bass note + 3 finger notes (drop-2 / drop-3 shapes) ---------------- */
// Upper set: the chord's tones without the root; with more than 3, drop the 5th, then the tone below the top one
// (13 → 3 ♭7 13, maj9 → 3 7 9, 7♭9 → 3 ♭7 ♭9, m7♭5 keeps its ♭5). Bass: the root in MIDI 40–52. The second bar of a
// 2-bar chord alternates the bass: the thumb on the fifth, root + 3rd + 7th (or 6th) in the fingers (Cmaj7/G).
// Fingers: every order of the 3 tones stacked upward from 52–66, top ≤ 74, span ≤ 14, ≥ a 4th above a bass under 45
// and ≥ a minor 3rd above a higher one (D3 F3 C4 E4); bass to top ≤ 29 (a 5-fret stretch).
const upperSet = q => {
  let u = QUALITIES[q].filter(i => i % 12 !== 0);
  if (u.length > 3 && u.includes(7)) u = u.filter(i => i !== 7);
  while (u.length > 3) u.splice(u.length - 2, 1);
  return u;
};
const altSet = q => {                               // the fifth-in-the-bass shape, or null (no perfect 5th, no 3rd)
  const iv = QUALITIES[q], third = iv.find(i => i === 3 || i === 4), sev = iv.find(i => i >= 9 && i <= 11);
  return iv.includes(7) && third != null && sev != null ? [0, third, sev] : null;
};
const PERMS = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]];
const CANDS = new Map();                            // `${root}:${q}:${alt}` → [[bass, u0, u1, u2]]
function candidates(root, q, alt) {
  const key = `${root}:${q}:${alt}`;
  if (CANDS.has(key)) return CANDS.get(key);
  const up = alt ? altSet(q) : upperSet(q), out = [];
  if (up) {
    const pcs = up.map(i => mod12(root + i)), bpc = alt ? root + 7 : root, basses = [];
    for (let m = 40 + mod12(bpc - 40); m <= 52; m += 12) basses.push(m);
    for (const pm of PERMS) for (let base = 52; base <= 66; base += 12) {
      const u = [];
      for (const k of pm) { let m = u.length ? u[u.length - 1] + 1 : base; m += mod12(pcs[k] - m); u.push(m); }
      if (u[0] > 66 || u[2] > 74 || u[2] - u[0] > 14) continue;
      for (const b of basses) if (u[0] - b >= (b < 45 ? 5 : 3) && u[2] - b <= 29) out.push([b, ...u]);
    }
  }
  CANDS.set(key, out);
  return out;
}
/** Cost: Σ|Δ| of the fingers + .4·|Δbass| + 2·max(0, |Δtop| − 4) + .5·|mean(fingers) − 64| + .35·|bass − 46|
 *  (+8 per semitone between neighbouring fingers). The same chord again costs nothing to stay, so every hit of a chord
 *  gets the same shape. The first chord (no prev) aims the fingers at 64 + the track's register (plan.register). */
export function guitarVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), alt = c.onset === false ? candidates(root, c.q, true) : [];
  const cands = alt.length ? alt : candidates(root, c.q, false), home = 64 + (prev ? 0 : plan.register);
  let best = null, bc = Infinity;
  for (const x of cands) {
    const [b, u0, u1, u2] = x;
    let k = 0.5 * Math.abs((u0 + u1 + u2) / 3 - home) + 0.35 * Math.abs(b - 46) + (u1 - u0 === 1 ? 8 : 0) + (u2 - u1 === 1 ? 8 : 0);
    if (prev && prev.length === 4) k += Math.abs(u0 - prev[1]) + Math.abs(u1 - prev[2]) + Math.abs(u2 - prev[3]) + 0.4 * Math.abs(b - prev[0]) + 2 * Math.max(0, Math.abs(u2 - prev[3]) - 4);
    if (k < bc) { bc = k; best = x; }
  }
  return best || [40 + mod12(root - 40), ...upperSet(c.q).map(i => 52 + mod12(root + i - 52)).sort((x, y) => x - y)];
}

/* ---------------- bass: root on 1, fifth on 3, pickups on the "2-and" and "4-and" (tied over the bar line p .5) ---------------- */
function bassLine({ plan, st, i, b, mode, chords, nextChords, chordAt, stop }) {
  const pc = c => mod12(plan.key.tonic + c.root), out = [];
  const add = (s, midi, vel, dur, slide = false) => { if (stop == null || s < stop) { out.push({ s, midi, vel, dur, slide }); return true; } return false; };
  const fifth = (c, m) => { const iv = QUALITIES[c.q], f = iv.includes(7) ? 7 : iv.includes(6) ? 6 : iv.includes(8) ? 8 : 7; return m + f <= 47 ? m + f : m + f - 12; };
  if (mode === 'whole' || mode === 'last' || (mode === 'outro' && b.j === 2)) {           // break, intro's last bar, final
    for (const c of chords) { const m = bassNote(pc(c), st.prevBass); add(c.start * 4, m, 0.8, mode === 'outro' ? 28 : c.beats * 4 * 0.95); st.prevBass = m; }
    return out;
  }
  const E = E_IX[plan.vibe.energy] ?? 1, f = plan.bass.factor, r = st.rS;
  const c0 = chordAt(0), c8 = chordAt(8), change = c8 !== c0;
  const r0 = bassNote(pc(c0), st.prevBass), three = change ? bassNote(pc(c8), r0) : fifth(c0, r0);
  if (st.bossaTie !== i) add(0, r0, 0.9, 6 * f);                                          // else the 4-and note rings on
  if (r() < [0.35, 0.6, 0.75][E]) add(6, three, 0.66, 1.8);
  add(8, three, 0.84, 6 * f);
  st.prevBass = three;
  const nx = nextChords && nextChords[0];
  if (r() < [0.45, 0.65, 0.8][E]) {
    const target = nx ? bassNote(pc(nx), three) : r0, approach = nx && pc(nx) !== pc(c8) && r() < 0.3;
    const tie = !approach && r() < 0.5, m = approach ? target + (r() < 0.6 ? -1 : 1) : target;
    if (add(14, m, 0.64, tie ? 2 + 6 * f : 1.8, !approach && r() < 0.08) && tie) st.bossaTie = i + 1;
    st.prevBass = target;
  }
  return out;
}

/* ---------------- the style ---------------- */
const CLAVE = 'x..x..x...x..x..', CLAVE23 = '..x..x..x..x..x.';
const F = [1, 2, 3], T = [0];                        // picks: the fingers (upper voices), the thumb (bass note)
export default {
  id: 'bossa', name: 'Bossa', desc: 'Nylon guitar and a straight bossa nova groove',
  vary: 'jazz',
  energy: {
    //          chill                         balanced                      upbeat
    bpm: [[64, 72], [72, 86], [82, 96]],
    swing: [[51, 53], [50, 52.5], [50, 52]],
    snareLag: [[0.002, 0.008], [0.001, 0.006], [0, 0.004]],
    groove: [
      [{ sparse: 1.6, nova: 1, brush: 1.2 }, { sparse: 1, nova: 1.2, brush: 1 }],
      [{ nova: 1.4, brush: 1, sparse: 0.6, partido: 0.3 }, { nova: 1.5, brush: 0.8, partido: 0.6, sparse: 0.3 }],
      [{ nova: 1.2, partido: 1.2, brush: 0.6 }, { nova: 1, partido: 1.4, brush: 0.5 }],
    ],
    stretch: [0.55, 0.4, 0.25],
    drumLevel: [0.85, 0.95, 1],
    kitLp: [[5000, 7000], [6000, 8500], [7000, 9500]],
    epLp: [[2600, 3800], [3200, 4800], [3800, 5600]],
    strum: [[0.01, 0.024], [0.007, 0.018], [0.005, 0.014]],
    epVel: [[0.55, 0.64], [0.58, 0.68], [0.58, 0.7]],
    comp: [
      { lazy: [0.9, 1.4], batida: [0.4, 1], dotted: [0.1, 0.4], joao: [0, 0.1] },
      { lazy: [0.2, 0.6], batida: [0.8, 1.4], dotted: [0.3, 0.8], joao: [0.2, 0.6] },
      { lazy: [0, 0.15], batida: [0.6, 1.2], dotted: [0.4, 0.9], joao: [0.6, 1.2] },
    ],
    push: [0.15, 0.28, 0.4],
    legato: [0.8, 0.7, 0.55],
    lead: [0.55, 0.65, 0.8],
    softLead: [0.7, 0.6, 0.5],                       // odds of the flute over the nylon lead
    tone: [[4500, 7000], [5500, 8500], [6500, 9500]],
    wowCents: [[5, 10], [4, 8], [3, 6]],
    introHats: [0.4, 0.55, 0.7],
    hatsFirst: [0.4, 0.3, 0.15],
    breakDrums: [0.4, 0.6, 0.85],
  },
  progressions: {
    major: {
      B1: [[0, 'maj7', 4], [2, '9', 4], [2, 'm9', 4], [7, '13', 4]],                         // Imaj7 · II9 · iim9 · V13
      B2: [[2, 'm9', 4], [7, '13', 4], [0, 'maj9', 4], [9, '7b9', 4]],                        // iim9 · V13 · Imaj9 · VI7♭9
      B3: [[0, 'maj9', 4], [10, '13', 4], [0, 'maj9', 4], [10, '9', 4]],                      // Imaj9 · ♭VII13 (twice)
      B4: [[0, '69', 4], [9, 'm9', 4], [2, 'm9', 4], [7, '7b9', 4]],                          // I6/9 · vim9 · iim9 · V7♭9
      B5: [[5, 'maj9', 4], [5, 'm6', 4], [4, 'm7', 2], [9, '7b9', 2], [2, 'm9', 2], [7, '13', 2]], // IV · iv6 · iii–VI · ii–V
      B6: [[0, 'maj7', 4], [1, 'dim7', 4], [2, 'm7', 4], [7, '9', 4]],                        // Imaj7 · ♯i°7 · iim7 · V9
    },
    minor: {
      b1: [[0, 'm9', 4], [5, 'm7', 4], [10, '9', 4], [3, 'maj7', 4]],                        // im9 · ivm7 · ♭VII9 · ♭IIImaj7
      b2: [[2, 'm7b5', 4], [7, '7b9', 4], [0, 'm6', 4], [0, 'm6', 4]],                        // iiø · V7♭9 · im6
      b3: [[0, 'm7', 4], [2, 'm7b5', 4], [7, '7b9', 4], [0, 'm9', 4]],                        // im7 · iiø · V7♭9 · im9
      b4: [[0, 'm9', 4], [5, '9', 4], [0, 'm9', 4], [5, '13', 4]],                            // Dorian: im9 · IV9 · im9 · IV13
      b5: [[8, 'maj7', 4], [7, '7b9', 4], [0, 'm7', 4], [0, 'm6', 4]],                        // ♭VImaj7 · V7♭9 · im7 · im6
      b6: [[0, 'm9', 4], [10, '9', 4], [8, 'maj7', 4], [7, '7b9', 4]],                        // im9 · ♭VII9 · ♭VImaj7 · V7♭9
    },
  },
  turnarounds: { major: [[2, 'm7', 2], [7, '7b9', 2]], minor: [[2, 'm7b5', 2], [7, '7b9', 2]] },
  grids: {
    //        0123456789abcdef
    nova: { K: 'x.....s.x.....s.', R: CLAVE, X: 'xsasxsasxsasxsas' },                             // surdo kick, clave, 16th shaker
    brush: { K: 'x.......x.......', S: 's.x.s.x.s.x.s.x.', R: CLAVE, H: '....x.......x...' },       // brush 8ths, hat foot on 2 & 4
    partido: { K: 'x.....x.x.....x.', R: CLAVE23, X: 'xsasxsasxsasxsas', H: 's.s.s.s.s.s.s.s.' },  // 2-3 clave, busier
    sparse: { K: 'x.......s.......', R: '...x......x.....', X: 'x.s.x.s.x.s.x.s.' },              // chill: half the clave
    break: { R: CLAVE, X: 'x.s.x.s.x.s.x.s.' },
  },
  fills: {
    rim: { roll: [[12, 0.6], [14, 0.75], [15, 0.9]], voice: 'rim' },                              // cross-stick pickup
    surdo: { roll: [[13, 0.55], [14, 0.7], [15, 0.85]], voice: 'tom' },                            // tom pickup into the 1
    breath: { stop: 12 },                                                                          // the band stops for beat 4
  },
  bVariation: false,
  // [step, length, vel, pick]: a full chord on 1 (the thumb rings ≥ 6 steps), fingers F, the thumb T on 3
  comps: {
    lazy: [[0, 6, 1], [6, 3, 0.8, F], [8, 8, 0.85, T], [11, 3, 0.75, F]],
    batida: [[0, 3, 1], [3, 2, 0.8, F], [6, 3, 0.86, F], [8, 8, 0.85, T], [10, 2, 0.8, F], [13, 3, 0.86, F]],
    dotted: [[0, 3, 1], [3, 3, 0.8, F], [6, 3, 0.86, F], [8, 8, 0.85, T], [9, 3, 0.8, F], [12, 4, 0.86, F]],
    joao: [[0, 3, 1], [3, 2, 0.8, F], [4, 4, 0.78, T], [6, 3, 0.86, F], [8, 4, 0.85, T], [10, 2, 0.8, F], [12, 4, 0.78, T], [13, 3, 0.86, F]],
  },
  keysLevel: 1.85,
  plan(plan, { r }) {
    const k = plan.drums.kit;
    plan.drums.kit = {
      kickF0: rnd(r, 95, 120, 1), kickF1: rnd(r, 45, 53, 1), kickDecay: rnd(r, 0.16, 0.22), brush: rnd(r, 0.1, 0.16),
      hatHP: rnd(r, 7000, 9000, 0), hatClosed: rnd(r, 0.009, 0.015), rimF: rnd(r, 520, 620, 0), shaker: rnd(r, 0.03, 0.045), lp: k.lp,
    };
    plan.bass.lp = rnd(r, 460, 600, 0);
    if (plan.lead) plan.lead.timbre = plan.lead.timbre === 'soft' ? 'flute' : 'nylon';
  },
  voicing: guitarVoicing,
  bass: bassLine,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    const ep = createNylon(ac, dry, wet, plan.ep, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur });
    ep.warm(trackChords(plan), 3);
    let lead;
    if (plan.lead && plan.lead.timbre === 'nylon') {
      const g = createNylon(ac, dry, wet, { lp: plan.ep.lp * 1.15 }, { ...o, cap: 6, r: sub(), stepDur: ctx.stepDur, pan: plan.lead.pan, spread: 0.3, level: 2, sendLevel: 0.38 });
      // melody notes within a 4th share a string (the new note damps the last)
      lead = { note: (t, m, v, d) => g.note(t, m, v, d, 0.12, 'finger', `s${Math.floor((m - 64) / 5)}`, 2), dispose: t => g.dispose(t) };
    } else if (plan.lead) lead = createFlute(ac, dry, wet, plan.lead, { ...o, stepDur: ctx.stepDur });
    return { ep, bass: createUpright(ac, dry, plan.bass, o), kit: createBossaKit(ac, dry, wet, plan.drums.kit, o), lead };
  },
};

/** the track's voicings, first bars first (progA, progB, the final tonic as in engine.js barChords), to render their
 *  plucks ahead of time */
function trackChords(plan) {
  const out = [], fin = { root: 0, q: plan.key.mode === 'minor' ? 'm9' : plan.seed % 2 ? 'maj9' : '69' };
  let prev = null;
  for (const [c, P] of playedChords(plan, fin)) { prev = guitarVoicing(null, prev, c, P); out.push(prev); }
  return out;
}
